/**
 * Editable label/value rows for an option-backed field (select, radio, checkbox list,
 * custom list). Shared by Admin → Field Builder (adding AND editing fields) and the
 * intake form's inline layout editor, so choices behave identically in both.
 *
 * `onChange` is always called with a plain array (never an updater function).
 *
 * THE `autoSlug` PROP IS LOAD-BEARING. Stored values are referenced by approval-flow
 * trigger conditions (e.g. the gated client-source Managing Partner sign-off), by
 * `visibleIf` reveal rules, and by every already-saved matter answer. When CREATING a
 * brand-new field (`autoSlug` true, the default) it is safe and convenient for the value
 * to track the label as the admin types. When EDITING an existing field (`autoSlug`
 * false — both edit surfaces pass this) a label edit must NEVER touch the stored value:
 * silently rewriting it would defuse approval gates and orphan existing answers with no
 * warning anywhere. In edit mode the value only auto-fills from the label on blur, and
 * only when the value is still empty (i.e. a row the admin just added).
 */

const slug = (label) =>
  String(label || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export default function OptionRowsEditor({
  options,
  onChange,
  minimumOptions = 0,
  legend = 'Choices',
  autoSlug = true,
}) {
  const rows = Array.isArray(options) ? options : [];
  const patch = (index, updates) =>
    onChange(rows.map((option, optionIndex) => (optionIndex === index ? { ...option, ...updates } : option)));

  return (
    <fieldset className="min-w-[20rem] text-xs text-slate-600 dark:text-slate-300">
      <legend className="mb-0.5">{legend}</legend>
      {!autoSlug && (
        <p className="mb-1 max-w-md text-amber-700 dark:text-amber-300">
          “Stored value” is what approval rules, show/hide conditions, and already-saved
          answers reference. Rename labels freely — but only change a stored value if you
          are sure nothing references it.
        </p>
      )}
      <div className="space-y-1">
        {rows.map((option, index) => (
          <div key={index} className="flex items-center gap-1">
            <input
              className="w-32 rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600"
              value={option.label}
              onChange={(event) => {
                const label = event.target.value;
                if (!autoSlug) {
                  // Editing an existing field: a label edit must never rewrite the value.
                  patch(index, { label });
                  return;
                }
                const priorSlug = slug(option.label);
                patch(index, {
                  label,
                  ...(!option.value || option.value === priorSlug ? { value: slug(label) } : null),
                });
              }}
              onBlur={() => {
                // Edit mode: fill an EMPTY value from the finished label, once. Never
                // touches a row that already has a value.
                if (!autoSlug && !option.value && option.label) {
                  patch(index, { value: slug(option.label) });
                }
              }}
              placeholder="Label"
              aria-label={`Choice ${index + 1} label`}
            />
            <input
              className="w-28 rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600"
              value={option.value}
              onChange={(event) => patch(index, { value: event.target.value })}
              placeholder="Stored value"
              aria-label={`Choice ${index + 1} value`}
            />
            <button
              type="button"
              onClick={() => onChange(rows.filter((_, optionIndex) => optionIndex !== index))}
              disabled={rows.length <= minimumOptions}
              className="px-1 text-red-600 hover:underline disabled:opacity-40 dark:text-red-400"
              aria-label={`Remove choice ${index + 1}`}
            >
              Remove
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => onChange([...rows, { label: '', value: '' }])}
        className="mt-1 text-firm-700 hover:underline dark:text-firm-100"
      >
        + Add choice
      </button>
    </fieldset>
  );
}
